/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiSideNavItem } from './side_nav_item';
import { EuiButtonEmpty } from '../button';
import { EuiTitle } from '../title';
import { EuiScreenReaderOnly } from '../accessibility';
import { htmlIdGenerator } from '../../services';
import { EuiHideFor, EuiShowFor } from '../responsive';
export class EuiSideNav extends Component {
  generateId = htmlIdGenerator('euiSideNav');
  static defaultProps = {
    items: [],
    mobileBreakpoints: ['xs', 's']
  };
  isItemOpen = item => {
    // The developer can force the item to be open.
    if (item.forceOpen) {
      return true;
    }

    // Of course a selected item is open.
    if (item.isSelected) {
      return true;
    }

    // The item has to be open if it has a child that's open.
    if (item.items) {
      return item.items.some(this.isItemOpen);
    }
    return false;
  };
  renderTree = (items, depth = 0) => {
    const {
      renderItem,
      truncate
    } = this.props;
    return items.map(item => {
      const {
        id,
        name,
        isSelected,
        items: childItems,
        icon,
        onClick,
        href,
        forceOpen,
        ...rest
      } = item;

      // Root items are always open.
      const isOpen = depth === 0 ? true : this.isItemOpen(item);
      let renderedItems;
      if (childItems) {
        renderedItems = this.renderTree(childItems, depth + 1);
      }

      // Act as an accordion only if item is not linked but has children (and not the root)
      const childrenOnly = depth > 0 && !onClick && !href && !!childItems;
      return <EuiSideNavItem isOpen={isOpen} isSelected={!childrenOnly && isSelected} isParent={!!childItems} icon={icon} onClick={onClick} href={href} items={renderedItems} key={id} depth={depth} renderItem={renderItem} truncate={truncate} childrenOnly={childrenOnly} {...rest}>
          {name}
        </EuiSideNavItem>;
    });
  };
  render() {
    const {
      className,
      items,
      toggleOpenOnMobile,
      isOpenOnMobile,
      mobileTitle,
      mobileBreakpoints,
      // Extract this one out so it isn't passed to <nav>
      renderItem,
      truncate,
      heading,
      headingProps = {},
      ...rest
    } = this.props;
    const classes = classNames('euiSideNav', className, {
      'euiSideNav-isOpenMobile': isOpenOnMobile
    });

    // To support the extra CSS needed to show/hide/animate the content,
    // We add a className for every breakpoint supported
    const contentClasses = classNames('euiSideNav__content', mobileBreakpoints?.map(breakpointName => `euiSideNav__contentMobile-${breakpointName}`));
    const sideNavContentId = this.generateId('content');
    const navContent = <div id={sideNavContentId} className={contentClasses}>
        {this.renderTree(items)}
      </div>;
    const {
      screenReaderOnly: headingScreenReaderOnly = false,
      element: HeadingElement = 'h2',
      ...titleProps
    } = headingProps;
    const hasMobileVersion = mobileBreakpoints && mobileBreakpoints.length > 0;
    const hasHeader = !!heading;
    let headingNode;
    const sharedHeadingProps = {
      id: headingProps?.id || this.generateId('heading'),
      className: headingProps?.className,
      'data-test-subj': headingProps?.['data-test-subj'],
      'aria-label': headingProps?.['aria-label']
    };
    if (hasHeader) {
      headingNode = <HeadingElement {...sharedHeadingProps}>{heading}</HeadingElement>;
      if (headingScreenReaderOnly) {
        headingNode = <EuiScreenReaderOnly>{headingNode}</EuiScreenReaderOnly>;
      } else {
        headingNode = <EuiTitle size="xs" {...titleProps} className={classNames('euiSideNav__heading', headingProps?.className)}>
            <HeadingElement {...sharedHeadingProps}>{heading}</HeadingElement>
          </EuiTitle>;
      }
    }
    let mobileNode;
    const breakpoints = mobileBreakpoints;
    if (hasMobileVersion) {
      mobileNode = <EuiShowFor sizes={breakpoints || 'none'}>
          <nav aria-labelledby={sharedHeadingProps.id} className={classes} {...rest}>
            <HeadingElement {...sharedHeadingProps}>
              <EuiButtonEmpty className="euiSideNav__mobileToggle" textProps={{
              className: 'euiSideNav__mobileToggleText'
            }} contentProps={{
              className: 'euiSideNav__mobileToggleContent'
            }} onClick={toggleOpenOnMobile} iconType="apps" iconSide="right" aria-controls={sideNavContentId} aria-expanded={isOpenOnMobile}>
                {mobileTitle || heading}
              </EuiButtonEmpty>
            </HeadingElement>
            {navContent}
          </nav>
        </EuiShowFor>;
    }
    return <>
        {mobileNode}
        <EuiHideFor sizes={breakpoints || 'none'}>
          <nav aria-labelledby={headingNode ? sharedHeadingProps.id : undefined} className={classes} {...rest}>
            {headingNode}
            {navContent}
          </nav>
        </EuiHideFor>
      </>;
  }
}
EuiSideNav.propTypes = {
  /**
       * Class names to be merged into the final `className` property.
       */
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Creates an associated heading element and uses the same node as default for `mobileTitle`
       */
  heading: PropTypes.node,
  /**
       * Adds a couple extra #EuiSideNavHeading props and extends the props of EuiTitle that wraps the `heading`
       */
  headingProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * The actual HTML heading element to wrap the `heading`.
       * Default is `h2`
       */
    element: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6", "span"]),
    /**
       * For best accessibility, `<nav>` elements should have a nested heading. But you can hide this element if it's redundent from something else (except on mobile).
       */
    screenReaderOnly: PropTypes.bool
  }),
  /**
       * When called, toggles visibility of the navigation menu at mobile responsive widths. The callback should set the `isOpenOnMobile` prop to actually toggle navigation visibility.
       */
  toggleOpenOnMobile: PropTypes.func,
  /**
       * If `true`, the navigation menu will be open at mobile device widths. Use in conjunction with the `toggleOpenOnMobile` prop.
       */
  isOpenOnMobile: PropTypes.bool,
  /**
       * A React node to render at mobile responsive widths, representing the title of this navigation menu.
       */
  mobileTitle: PropTypes.node,
  /**
       * Array of breakpoint names for when to show the mobile version.
       * Set to `undefined` to remove responsive behavior
       */
  mobileBreakpoints: PropTypes.arrayOf(PropTypes.any.isRequired),
  /**
       *  An array of #EuiSideNavItem objects. Lists navigation menu items.
       */
  items: PropTypes.arrayOf(PropTypes.shape({
    /**
       * A value that is passed to React as the `key` for this item
       */
    id: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
    /**
       * If set to true it will force the item to display in an "open" state at all times.
       */
    forceOpen: PropTypes.bool,
    /**
       * Array containing additional item objects, representing nested children of this navigation item.
       */
    items: PropTypes.arrayOf(PropTypes.shape({
      /**
         * A value that is passed to React as the `key` for this item
         */
      id: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
      /**
         * If set to true it will force the item to display in an "open" state at all times.
         */
      forceOpen: PropTypes.bool,
      /**
         * Array containing additional item objects, representing nested children of this navigation item.
         */
      items: PropTypes.arrayOf(PropTypes.any.isRequired),
      /**
         * React node representing the text to render for this item (usually a string will suffice).
         */
      name: PropTypes.node.isRequired,
      /**
         * Function overriding default rendering for this navigation item — when called, it should return a React node representing a replacement navigation item.
         */
      renderItem: PropTypes.func
    }).isRequired),
    /**
       * React node representing the text to render for this item (usually a string will suffice).
       */
    name: PropTypes.node.isRequired,
    /**
       * Function overriding default rendering for this navigation item — when called, it should return a React node representing a replacement navigation item.
       */
    renderItem: PropTypes.func
  }).isRequired).isRequired,
  /**
       * Overrides default navigation menu item rendering. When called, it should return a React node representing a replacement navigation item.
       */
  renderItem: PropTypes.func,
  /**
       * Truncates the text of all items to stick to a single line
       */
  truncate: PropTypes.bool
};
try {
  EuiSideNav.__docgenInfo = {
    tags: {},
    filePath: '/Users/briannahall/Documents/EUI/eui/src/components/side_nav/side_nav.tsx',
    description: '',
    displayName: 'EuiSideNav',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: 'Class names to be merged into the final `className` property.',
        name: 'className',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string | number | boolean | ComponentSelector | SerializedStyles | CSSObject | ArrayInterpolation<Theme> | FunctionInterpolation<...> | ({ ...; } & string)'
        }
      },
      children: {
        defaultValue: null,
        description: '`children` are not rendered. Use `items` to specify navigation items instead.',
        name: 'children',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      heading: {
        defaultValue: null,
        description: 'Creates an associated heading element and uses the same node as default for `mobileTitle`',
        name: 'heading',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number | boolean | ReactElement | Iterable<ReactNode> | ReactPortal'
        }
      },
      headingProps: {
        defaultValue: null,
        description: 'Adds a couple extra #EuiSideNavHeading props and extends the props of EuiTitle that wraps the `heading`',
        name: 'headingProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & Partial<EuiTitleProps> & { element?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span"; screenReaderOnly?: boolean; }'
        }
      },
      toggleOpenOnMobile: {
        defaultValue: null,
        description: 'When called, toggles visibility of the navigation menu at mobile responsive widths. The callback should set the `isOpenOnMobile` prop to actually toggle navigation visibility.',
        name: 'toggleOpenOnMobile',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      isOpenOnMobile: {
        defaultValue: null,
        description: 'If `true`, the navigation menu will be open at mobile device widths. Use in conjunction with the `toggleOpenOnMobile` prop.',
        name: 'isOpenOnMobile',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      mobileTitle: {
        defaultValue: null,
        description: 'A React node to render at mobile responsive widths, representing the title of this navigation menu.',
        name: 'mobileTitle',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number | boolean | ReactElement | Iterable<ReactNode> | ReactPortal'
        }
      },
      mobileBreakpoints: {
        defaultValue: {
          value: "['xs', 's']"
        },
        description: 'Array of breakpoint names for when to show the mobile version.\n' + 'Set to `undefined` to remove responsive behavior',
        name: 'mobileBreakpoints',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string[]'
        }
      },
      items: {
        defaultValue: {
          value: '[]'
        },
        description: 'An array of #EuiSideNavItem objects. Lists navigation menu items.',
        name: 'items',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiSideNavItemType<T>[]'
        }
      },
      renderItem: {
        defaultValue: null,
        description: 'Overrides default navigation menu item rendering. When called, it should return a React node representing a replacement navigation item.',
        name: 'renderItem',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'RenderItem<T>'
        }
      },
      truncate: {
        defaultValue: null,
        description: 'Truncates the text of all items to stick to a single line',
        name: 'truncate',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/side_nav/side_nav.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps']
  };
} catch (e) {}